import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Grid, Box, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "../../context/Auth";
import { FormControl, TextField } from "@mui/material";
import Logo from "assets/images/logos/logo_blue.png";
import darrenFaulknerImg from "assets/images/darren-faulkner-3FzYpZjE5Ec-unsplash.jpg";
import LoadingOverlay from "components/LoadingOverlay";
import { Link as MUILink } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Define Zod schema for form validation
const signUpSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

type SignUpFormData = z.infer<typeof signUpSchema>;

function Cover(): JSX.Element {
  const navigate = useNavigate();
  const { register: registerUser, loading, isAuthenticated, error, setError } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: ""
    }
  });

  const onSubmit = async (data: SignUpFormData) => {
    try {
      await registerUser({
        name: data.name,
        email: data.email,
        password: data.password
      });
    } catch (error: any) {
      setError(new Error(error));
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/plans");
    }
  }, [isAuthenticated, navigate]);

  return (
    <Grid container height={"100vh"}>
      <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loading ?
            <LoadingOverlay />
            : <img
              key={"title"}
              src={Logo}
              width={300}
              alt={"CityView CMS Logo"}
            />}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            Create An Account
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            Already have an account?{" "}
            <MUILink component={Link} to="/login" color="blue">
              Login Here
            </MUILink>
          </Typography>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <MDBox pt={4} pb={3} px={12}>
            <Grid container justifyContent="center">
              {error && (
                <Grid item xs={12}>
                  <MDTypography style={{ textAlign: "center" }} variant="body2" color="error">
                    {error.message}
                  </MDTypography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2">Name</Typography>
                <FormControl fullWidth>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={2}>
                <Typography variant="body2">Email</Typography>
                <FormControl fullWidth>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={2}>
                <Typography variant="body2">Password</Typography>
                <FormControl fullWidth>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="password"
                        variant="outlined"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={2}>
                <Typography variant="body2">Confirm Password</Typography>
                <FormControl fullWidth>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="password"
                        variant="outlined"
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={2}>
                <MDButton
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="info"
                  fullWidth
                >
                  Sign Up
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </form>
      </Grid>
      <Grid item md={8} sx={{
        backgroundImage: `url(${darrenFaulknerImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }} />
    </Grid>
  );
}

export default Cover;
